import { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import Input from "../ui/Input";
import Select from "../ui/Select";
import { tl } from "../../utils/format";
import { useHistory } from "react-router-dom";
import AuthContext from "../../Context/AuthContext";
import data2 from "../../data/lcwkonkur.json";
import magazalar from "../../data/lcwmagaza.json";
import Map from "../results/mapLCW";

export default function NewPlanPage({
  data = { advertisers: [] },
  initialBrandId = null,
  onCancel,
  onSaved,
  saveUrl = "https://opt-api.vercel.app/NewPlan",
}) {
  // Excel verisi için state
  const [excelData, setExcelData] = useState([]);
  const [loadingExcel, setLoadingExcel] = useState(true);
  const [successPopup, setSuccessPopup] = useState(false);


  // Excel dosyasını yükle
  useEffect(() => {
    const loadExcel = async () => {
      try {
        const response = await fetch("/data/my.xlsx");
        const arrayBuffer = await response.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);
        setExcelData(jsonData);
      } catch (error) {
        console.error("Excel dosyası yüklenirken hata:", error);
      } finally {
        setLoadingExcel(false);
      }
    };
    loadExcel();
  }, []);

  // Benzersiz ünite listesi
  const unitOptions = useMemo(() => {
    const units = [...new Set(excelData.map((row) => row["Ünite"]).filter(Boolean))];
    return ["", ...units];
  }, [excelData]);

  // Üniteye göre adres listesi
  const getAddressesByUnit = (unit) => {
    if (!unit) return [""];
    const addresses = excelData
      .filter((row) => row["Ünite"] === unit)
      .map((row) => row["Adres"])
      .filter(Boolean);
    return ["", ...addresses];
  };

  // Adrese göre detayları getir
  const getDetailsByAddress = (address) => {
    const row = excelData.find((r) => r["Adres"] === address);
    if (!row) return { unitNumber: 0, faces: 0, unitPrice: 0 };
    return {
      unitNumber: Number(row["Ünite Sayısı"]) || 0,
      faces: Number(row["Face"]) || 0,
      unitPrice: Number(row["Fiyat"]) || 0,
    };
  };

  // markalar
  const brandOptions = useMemo(() => {
    const out = [];
    (data.advertisers || []).forEach((adv) =>
      (adv.brands || []).forEach((b) =>
        out.push({ brandId: b.id, label: `${adv.name} / ${b.name}` })
      )
    );
    return out;
  }, [data]);

  const makeEmptyRow = () => ({
    id: String(Date.now()) + Math.random().toString(36).slice(2, 8),
    selectedUnit: "",
    address: "",
    startDate: "",
    endDate: "",
    unit: "",
    unitNumber: 0,
    faces: 0,
    unitPrice: 0,
    vendor: "",
  });

  const [brandId, setBrandId] = useState(() => initialBrandId || brandOptions[0]?.brandId || "");
  const [name, setName] = useState("");
  const [currency, setCurrency] = useState("TRY");
  const [notes, setNotes] = useState([]);
  const [noteDraft, setNoteDraft] = useState("");
  const [rows, setRows] = useState([makeEmptyRow()]);
  const [saving, setSaving] = useState(false);
  const history = useHistory();
  const { userData } = useContext(AuthContext);

  const combinedData = useMemo(() => {
    const normalizedKonkur = (Array.isArray(data2) ? data2 : []).map((item) => ({
      ...item,
      Adres: item.ADRES || item.Adres || "",
      "Liste Tür": item["Liste  Tür"] || item["Liste Tür"] || "",
    }));

    const storesAsRaw = (Array.isArray(magazalar) ? magazalar : []).map((m, idx) => ({
      Adres: m.name || `Mağaza #${idx + 1}`,
      "Liste Tür": "LCW Mağazaları",
      latitude: String(m.latitude),
      longitude: String(m.longitude),
    }));

    return [...normalizedKonkur, ...storesAsRaw];
  }, []);

  useEffect(() => {
    if (!brandId && brandOptions[0]) setBrandId(brandOptions[0].brandId);
  }, [brandOptions, brandId]);

  // hesap: satır bütçesi ve toplam (Excel'deki fiyat direkt budget olarak kullanılıyor)
  const rowBudget = (r) => Number(r.unitPrice) || 0;
  const totals = rows.reduce((acc, r) => acc + rowBudget(r), 0);

  // satır işlemleri
  const updateRow = (id, patch) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const addRow = () => setRows((rs) => [...rs, makeEmptyRow()]);
  const removeRow = (id) => setRows((rs) => rs.filter((r) => r.id !== id));

  // Ünite değiştiğinde
  const handleUnitChange = (rowId, newUnit) => {
    updateRow(rowId, {
      selectedUnit: newUnit,
      address: "",
      unitNumber: 0,
      faces: 0,
      unitPrice: 0,
    });
  };

  // Adres değiştiğinde otomatik doldur
  const handleAddressChange = (rowId, newAddress) => {
    const details = getDetailsByAddress(newAddress);
    updateRow(rowId, {
      address: newAddress,
      unitNumber: details.unitNumber,
      faces: details.faces,
      unitPrice: details.unitPrice,
    });
  };

  // notlar
  const addNote = () => {
    const t = noteDraft.trim();
    if (!t) return;
    setNotes((n) => [...n, t]);
    setNoteDraft("");
  };
  const removeNote = (i) => setNotes((n) => n.filter((_, idx) => idx !== i));

  // doğrulama
  const validate = () => {
    const errs = [];
    if (!name.trim()) errs.push("Plan adı (Campaign) gerekli.");
    rows.forEach((r, i) => {
      if (!r.address) errs.push(`Satır ${i + 1}: Address gerekli`);
      if (!r.startDate) errs.push(`Satır ${i + 1}: Start Date gerekli`);
      if (!r.endDate) errs.push(`Satır ${i + 1}: End Date gerekli`);
      if (r.startDate && r.endDate && new Date(r.endDate) < new Date(r.startDate)) {
        errs.push(`Satır ${i + 1}: End Date, Start Date'ten önce olamaz`);
      }
    });
    return errs;
  };

  // payload
  const buildPayload = () => ({
    name,
    brandId: userData.user.user.username,
    currency,
    notes: notes.join("\n"),
    addresses: rows.map((r) => ({
      address: r.address,
      startDate: r.startDate ? new Date(r.startDate) : undefined,
      endDate: r.endDate ? new Date(r.endDate) : undefined,
      period: 1,
      unit: r.selectedUnit,
      unitNumber: Number(r.unitNumber) || 0,
      faces: Number(r.faces) || 0,
      unitPrice: Number(r.unitPrice) || 0,
      vendor: r.vendor,
    })),
  });

  // kaydet
  const handleSave = async () => {
    const errs = validate();
    if (errs.length) {
      alert("Lütfen hataları düzeltin:\n\n" + errs.join("\n"));
      return;
    }
    const payload = buildPayload();
    setSaving(true);
    try {
      const res = await axios.post(saveUrl, payload);
      onSaved?.(res.data);
      setSuccessPopup(true);

      setTimeout(() => {
        history.push("/homepage");
      }, 2000);

    } catch (err) {
      console.error(err);
      alert("Kaydetme sırasında hata: " + (err.response?.data?.hata || err.message || err));
    } finally {
      setSaving(false);
    }
  };

  if (loadingExcel) {
    return <div className="p-4">Excel verisi yükleniyor...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 bg-white p-4 rounded shadow-sm">
        <button onClick={() => history.push("/homepage")} className="px-3 py-1 rounded border text-sm">
          ‹ Geri
        </button>
        <div className="ms-auto flex items-center gap-3">
          <div className="text-sm text-slate-600">
            Toplam: <span className="font-medium">{tl(totals)}</span>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="h-10 px-4 rounded bg-brand-600 text-white hover:bg-brand-700"
          >
            {saving ? "Kaydediliyor..." : "Kaydet"}
          </button>
        </div>
      </div>

      {/* Meta */}
      <div className="card p-4 bg-white rounded shadow-sm">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <label className="block text-sm">
            <div className="text-slate-600 mb-1">Kampanya İsmi</div>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Örn: İstanbul Kasım Outdoor" />
          </label>
        </div>
      </div>

      {/* Satırlar */}
      <div className="card p-0 overflow-hidden bg-white rounded shadow-sm">
        <div className="p-3 flex items-center justify-between">
          <div className="font-medium">Satırlar</div>
          <button onClick={addRow} className="h-9 px-3 rounded bg-emerald-600 text-white">
            + Satır Ekle
          </button>
        </div>

        <div className="overflow-auto">
          <table className="min-w-[1100px] w-full text-sm">
            <thead>
              <tr className="text-slate-600 bg-slate-50">
                {["Ünite", "Adres", "Başlangıç Tarihi", "Bitiş Tarihi", "Ünite Sayısı", "Face", "Fiyat", "Vendor", ""].map(
                  (h) => (
                    <th key={h} className="p-2 text-left">
                      {h}
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr key={r.id} className={idx % 2 ? "bg-slate-50/50" : ""}>
                  <td className="p-2">
                    <Select value={r.selectedUnit} onChange={(e) => handleUnitChange(r.id, e.target.value)}>
                      {unitOptions.map((u) => (
                        <option key={u} value={u}>
                          {u || "Ünite seçiniz"}
                        </option>
                      ))}
                    </Select>
                  </td>
                  <td className="p-2">
                    <Select
                      value={r.address}
                      onChange={(e) => handleAddressChange(r.id, e.target.value)}
                      disabled={!r.selectedUnit}
                    >
                      {getAddressesByUnit(r.selectedUnit).map((a) => (
                        <option key={a} value={a}>
                          {a || "Adres seçiniz"}
                        </option>
                      ))}
                    </Select>
                  </td>
                  <td className="p-2">
                    <Input type="date" value={r.startDate} onChange={(e) => updateRow(r.id, { startDate: e.target.value })} />
                  </td>
                  <td className="p-2">
                    <Input type="date" value={r.endDate} onChange={(e) => updateRow(r.id, { endDate: e.target.value })} />
                  </td>
                  <td className="p-2">
                    <span className="px-2 py-1 bg-slate-100 rounded">{r.unitNumber}</span>
                  </td>
                  <td className="p-2">
                    <span className="px-2 py-1 bg-slate-100 rounded">{r.faces}</span>
                  </td>
                  <td className="p-2 font-medium">{tl(rowBudget(r))}</td>
                  <td className="p-2">
                    <Input value={r.vendor} onChange={(e) => updateRow(r.id, { vendor: e.target.value })} placeholder="Vendor" />
                  </td>
                  <td className="p-2">
                    <button onClick={() => removeRow(r.id)} className="text-rose-600">
                      Sil
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-slate-50">
                <td colSpan={6} className="text-right p-2 font-semibold">
                  Total
                </td>
                <td className="p-2 font-semibold">{tl(totals)}</td>
                <td colSpan={2}></td>
              </tr>
            </tfoot>
          </table>
          <Map rawData={combinedData} zoom={12} />
        </div>
      </div>
      {successPopup && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center backdrop-blur-sm">
          <div className="bg-white rounded-2xl p-8 w-[360px] text-center animate-scale-in shadow-2xl">

            {/* <div className="text-6xl mb-4 animate-bounce">🎉</div> */}

            <h2 className="text-xl font-semibold text-emerald-600">
              Plan Başarıyla Kaydedildi
            </h2>

            <p className="text-slate-500 text-sm mt-2">
              Ana sayfaya yönlendiriliyorsunuz...
            </p>

            <div className="mt-5 h-1 w-full bg-slate-100 rounded overflow-hidden">
              <div className="h-full bg-emerald-500 animate-progress"></div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
